// KÖZÖS szerkesztő-héj — jobb oldali beúszó sáv (right-side sheet) a create/edit űrlapokhoz. Ez az iparági
// best-practice minta a listákhoz kötött gyors szerkesztéshez: a lista kontextusban marad (nem navigálunk el),
// a form a jobb szélről úszik be. A megerősítés/törlés/figyelmeztetés MARAD modal (ConfirmDialog) — csak a
// tartalmi szerkesztő-formok kerülnek sávba. (Kivétel: az ügyfelek/fiókok szerkesztése külön full-page — ott a
// komplex, kétoszlopos entitás indokolja; ezt a két modult NEM ez a komponens szolgálja ki.)
//
// Felépítés: fejléc (cím + opcionális leírás) · GÖRGETHETŐ törzs (a mezők) · ragadós lábléc (akciók). Az egész
// egy `<form>` → a lábléc `type="submit"` gombja természetesen elsüti (a react-hook-form `handleSubmit`-je az
// `onSubmit`-en). Reszponzív: mobilon teljes szélesség, desktopon fix (~32rem). A Radix Dialog ad fókusz-csapdát,
// Escape-et és a11y-t. A `busy` (mentés folyamatban) alatt a bezárás/Escape/overlay-katt le van tiltva (nem
// veszítünk el félig-mentett állapotot). A `dirty` (mentetlen változás) mellett a külső bezárás (mellékattintás,
// Escape, X) előbb rákérdez — enélkül egy félrekattintás figyelmeztetés nélkül eldobta a kitöltött űrlapot.
import { XMarkIcon } from '@heroicons/react/24/outline';
import { type FormEvent, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';

import { Sheet, SheetClose, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';

export interface FormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  /** A react-hook-form `handleSubmit(...)` wrappere (a lábléc submit-gombja süti el). */
  onSubmit?: ((e: FormEvent<HTMLFormElement>) => void) | undefined;
  /** Az űrlap-mezők (`<Form {...form}>` alá rendelve a hívónál). */
  children: ReactNode;
  /** Ragadós lábléc-akciók (pl. Mégse + Mentés). A submit-gomb `type="submit"`. */
  footer?: ReactNode | undefined;
  /** Mentés folyamatban → tiltjuk a külső bezárást (Escape/overlay/X), hogy ne vesszen el az állapot. */
  busy?: boolean | undefined;
  /**
   * Mentetlen változás van (pl. a react-hook-form `formState.isDirty`) → a KÜLSŐ bezárás (mellékattintás, Escape,
   * X) előbb megerősítést kér. A hívó saját gombjai (Mégse, sikeres mentés) a `onOpenChange`-et közvetlenül hívják,
   * azokat nem érinti. Alapból ki.
   */
  dirty?: boolean | undefined;
  /** Szélesség-felülírás (alap: mobil teljes, desktop ~32rem). */
  className?: string | undefined;
}

export function FormSheet({
  open,
  onOpenChange,
  title,
  description,
  onSubmit,
  children,
  footer,
  busy = false,
  dirty = false,
  className,
}: FormSheetProps): JSX.Element {
  const { t } = useTranslation();
  const [confirmClose, setConfirmClose] = useState(false);
  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(next) => {
          // Mentés közben nem engedjük a külső bezárást (csak explicit gombbal).
          if (busy && !next) return;
          // mentetlen változásnál előbb rákérdezünk — a döntés a megerősítő dialógusé
          if (dirty && !next) {
            setConfirmClose(true);
            return;
          }
          onOpenChange(next);
        }}
      >
        <SheetContent
          side="right"
          // A záró X-et a FEJLÉC-sorban adjuk (a címmel egy vonalban, a dialog-minta szerint) → a beépített
          // abszolút X-et elrejtjük.
          hideClose
          // A SZÉLESSÉG a `SheetContent`-é (egy hely az egész appra) — itt CSAK a felület és a belső térköz.
          // A háttér EXPLICIT a sidebar/lista felületére állítva (`--app-surface-glass`: sötétben near-black
          // #161618, világosban #fff) — utilities-réteg, ezért biztosan felülír minden rejtett `bg`-t, és a
          // beúszó pontosan olyan sötét, mint a menü-sidebar és a listaoldalak.
          // A hívó `className`-je MERGE-elődik (nem írja felül a felületet): korábban `className ?? default`
          // volt, ezért egyetlen extra osztály elvitte a hátteret és a paddinget is.
          className={cn('text-foreground gap-0 bg-[var(--app-surface-glass)] p-0', className)}
          // Mentés közben az Escape-et is elnyeljük (a Radix onEscapeKeyDown + onInteractOutside).
          onEscapeKeyDown={(e) => busy && e.preventDefault()}
          onInteractOutside={(e) => busy && e.preventDefault()}
          onPointerDownOutside={(e) => busy && e.preventDefault()}
        >
          {/* A submit NEM buborékolhat ki: a sheet portálban renderel, de a React-esemény a KOMPONENS-fán halad,
            tehát egy űrlapon belül nyitott sheet (pl. a fiók-lap Domainek / Kapcsolattartók szekciója) beküldése
            a KÜLSŐ űrlapot is beküldte volna — mérve: a domain mentése a fiók mentését is elindította. */}
          <form
            onSubmit={(e) => {
              e.stopPropagation();
              onSubmit?.(e);
            }}
            className="flex h-full min-h-0 flex-col"
          >
            {/* Fejléc — a cím és a záró X EGY sorban (a X a FŐ címmel függőlegesen egy vonalban); az opcionális
              leírás KÜLÖN sorban, alatta (nem tolja el a X-et). Mentés közben a X tiltva (nem veszítünk
              félig-mentett állapotot). */}
            <div className="shrink-0 px-5 py-4">
              <div className="flex items-center justify-between gap-4">
                <SheetTitle className="min-w-0 truncate">{title}</SheetTitle>
                <SheetClose
                  disabled={busy}
                  className="text-muted-foreground focus-visible:ring-ring hover:bg-accent hover:text-foreground -mr-1.5 inline-flex size-7 shrink-0 items-center justify-center rounded-md opacity-80 transition-colors hover:opacity-100 focus:outline-none focus-visible:ring-1 disabled:pointer-events-none disabled:opacity-40"
                >
                  <XMarkIcon className="size-4" />
                  <span className="sr-only">{t('common.close')}</span>
                </SheetClose>
              </div>
              {description && (
                <Text variant="secondary" as="p" className="mt-0.5">
                  {description}
                </Text>
              )}
            </div>

            {/* Görgethető törzs — a mezők. */}
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">{children}</div>

            {/* Ragadós lábléc — akciók. */}
            {footer && (
              <div className="shrink-0 px-5 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3">
                {/* A footer-gombok közös min-szélessége → a rövidebb címkéjű (pl. „Mégse") sem lesz keskenyebb,
                  mint a mentés → mindkettő azonos méretű (a modál-mintát követve). */}
                <div className="flex items-center justify-end gap-2 [&>button]:min-w-20">
                  {footer}
                </div>
              </div>
            )}
          </form>
        </SheetContent>
      </Sheet>
      <ConfirmDialog
        open={confirmClose}
        onOpenChange={setConfirmClose}
        tone="warning"
        title={t('common.unsavedChanges.title')}
        description={t('common.unsavedChanges.desc')}
        confirmLabel={t('common.unsavedChanges.discard')}
        cancelLabel={t('common.unsavedChanges.keepEditing')}
        onConfirm={() => {
          setConfirmClose(false);
          onOpenChange(false);
        }}
      />
    </>
  );
}
